import React, { useState, useMemo, useEffect, useRef } from 'react';
import { NodeData, CanvasNode, type PickedReference } from '../../types';
import type { ReferenceChoice } from '../../utils/workspaceEdit';
import { isPickedInputId } from '../../utils/pickedReferences';
import { useAddReferences, type ReferenceSharing } from './AddReferencesButton';
import { InputTile, SendGroup, SettingChip, SettingsRow, TileRemoveButton, promptFieldClass, type ChipOption } from './composer/Composer';
import { PromptTagLayer, PromptTagPicker, usePromptTags, type PromptTagRef } from './composer/PromptTags';
import { imageTag } from '../../utils/promptMentions';
import { imagePicturesSent } from '../../utils/nodeReferenceTags';
import { PROVIDER_NAMES, shortModelLabel } from './composer/modelLabels';
import { GlassOrb } from '../GlassOrb';
import { useAutoGrowTextarea } from '../../hooks/useAutoGrowTextarea';
import { useFitNodeHeight } from '../../hooks/useFitNodeHeight';
import { AI_MODELS, getImageModels, calculateCost } from '../../models/config';
import { imageSettingsOf } from '../../utils/generatorSettings';
import { Image as ImageIcon, Loader2, Sparkles, AlertCircle, Download, ChevronLeft, ChevronRight, Twitter, RectangleHorizontal, Scan, Gauge, Images, SlidersHorizontal, Workflow } from 'lucide-react';
import { ShareToTwitterModal } from '../ShareToTwitterModal';
import { useFileUrls } from '../../hooks/useFileUrl';
import { uploadWithDeduplication, isFileReference } from '../../utils/fileRegistry';
import { FileImage } from '../FileImage';
import { loadMediaAsBase64 } from '../../utils/mediaLoader';
import { autoSaveFile, isAutoSaveReady, registerLocalFileMapping, CustomWorkflow } from '../../utils/localSave';
import { getComfyUIClient, buildTxt2ImgWorkflow, buildImg2ImgWorkflow, buildFluxWorkflow, buildFluxUnetWorkflow, aspectRatioToSize, convertWorkflowToApi } from '../../utils/comfyui';
import { veniceGenerateImage, veniceEditImage } from '../../utils/venice';
import { veniceSafeModeOn } from '../../utils/veniceSafeMode';
import { VeniceBalanceLabel } from '../VeniceBalance';
import { refreshVeniceBalance } from '../../hooks/useVeniceBalance';
import { PARALLEL_UPLOAD_BYTES, fitPicturesToRequest, referenceStep, requestBytes } from '../../utils/veniceRequestSize';


/**
 * One file-name stamp per image of a batch. They share a random base and count up from it,
 * so two images of one batch can never get the same name and overwrite each other.
 */
const batchStamps = (count: number): number[] => {
  const base = Date.now() + Math.floor(Math.random() * 1000);
  return Array.from({ length: count }, (_, index) => base + index);
};

interface ImageGeneratorNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  inputImages?: { id: string, url: string, label?: string }[];
  inputEntities?: CanvasNode[];
  onRemoveConnection?: (connectionId: string) => void;
  googleApiKey?: string;
  openAiApiKey?: string;
  veniceApiKey?: string;
  onRequestApiKey?: (provider?: 'google' | 'openai' | 'venice') => void;
  userId?: string; // For resolving file:// references
  workspaceName?: string; // For storage paths
  nodeId?: string; // For file registry
  onAutoSaveStatus?: (type: 'local' | 'cloud' | 'both', message: string) => void;
  onAutoSaveFile?: (fileData: string | Blob, fileName: string, category?: 'Generated_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads', fileExtension?: string) => Promise<void>;
  customWorkflows?: CustomWorkflow[];
  /** Current node box; the node grows with the prompt field while typing, and when its options would not fit. */
  size?: { width: number; height: number };
  onResize?: (id: string, newSize: { width: number; height: number }) => void;
  isViewOnly?: boolean;
  ownerUserId?: string;
  /** The workspace's media, read when the reference list opens. */
  getReferenceChoices?: () => ReferenceChoice[];
  /** Stores the references chosen from that list on the node. */
  onPickedReferencesChange?: (next: PickedReference[]) => void;
  /** In a project: the list also offers the Reference nodes of its other workspaces. */
  referenceSharing?: ReferenceSharing;
  /** Merges a generation's result into the node's live data, or holds it for this workspace when another is open. */
  onDataPatch?: (id: string, patch: Partial<NodeData>, guard?: (current: NodeData) => boolean) => boolean;
}

/** Common step counts for local models, with the current one when it is not among them. */
const STEP_CHOICES = [1, 2, 4, 6, 8, 12, 16, 20, 25, 30, 40, 50];
const stepOptions = (current: number): ChipOption<string>[] =>
  [...new Set([...STEP_CHOICES, current])].sort((a, b) => a - b).map(n => ({ value: String(n), label: `${n} steps` }));

export const ImageGeneratorNode: React.FC<ImageGeneratorNodeProps> = ({
  data,
  onDataChange,
  inputImages = [],
  inputEntities = [],
  onRemoveConnection,
  googleApiKey,
  openAiApiKey,
  veniceApiKey,
  onRequestApiKey,
  userId,
  workspaceName = 'Untitled',
  nodeId,
  onAutoSaveStatus,
  onAutoSaveFile,
  customWorkflows = [],
  size,
  onResize,
  isViewOnly = false,
  ownerUserId,
  getReferenceChoices,
  onPickedReferencesChange,
  referenceSharing,
  onDataPatch,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  // The prompt field fills the room the options leave and grows the node while typing; the node
  // grows by itself only when even the smallest field would not fit
  const fitRef = useRef<HTMLDivElement>(null);
  useFitNodeHeight(fitRef, nodeId, size, onResize);
  const { markUserEdit: markPromptEdit } = useAutoGrowTextarea(promptRef, data.prompt || '', {
    max: 360,
    onUserGrow: (delta) => { if (nodeId && onResize && size) onResize(nodeId, { width: size.width, height: size.height + delta }); },
  });
  const [missingKeyType, setMissingKeyType] = useState<'google' | 'openai' | 'venice'>('google');
  const [showTwitterModal, setShowTwitterModal] = useState(false);

  // Reset stuck generating state on mount - use functional update to avoid stale closure
  useEffect(() => {
    if (data.generationStatus === 'generating' && !isGenerating) {
      // Only reset if we're not actually generating (avoids resetting during active generation)
      console.log('Resetting stuck generation status on mount');
      onDataChange({ ...data, generationStatus: 'idle' });
    }
  }, []); // Only run on mount
  
  // Ref to track cancellation
  const isCancelledRef = useRef(false);

  // Initialize selection based on current URI
  const [selectedImageIndex, setSelectedImageIndex] = useState(() => {
      if (data.generatedImages && data.generatedImageUri) {
          const idx = data.generatedImages.indexOf(data.generatedImageUri);
          return idx >= 0 ? idx : 0;
      }
      return 0;
  });

  // Default values
  const prompt = data.prompt || '';
  
  const savedModelId = data.imageGenConfig?.model;
  const currentModelId = (savedModelId && AI_MODELS[savedModelId]) 
      ? savedModelId 
      : 'gemini-2.5-flash-image';
  
  // Get Model Definition
  const modelDef = AI_MODELS[currentModelId] || AI_MODELS['gemini-2.5-flash-image'];
  // Pictures from the workspace can be used as references when the model takes them
  const canPickReferences = !isViewOnly && !!modelDef.supportsImageReference && !!getReferenceChoices && !!onPickedReferencesChange;
  // How many of the pictures the model is sent, in order (as generateImage sends them): the
  // prompt names them @Image1, @Image2… (utils/nodeReferenceTags.ts, shared with the Claude connector)
  const referencesSent = imagePicturesSent(modelDef);
  const tagRefs = useMemo<PromptTagRef[]>(
      () => inputImages.slice(0, referencesSent).map((img, i) => ({ tag: imageTag(i), label: img.label || `Reference ${i + 1}`, kind: 'image', url: img.url })),
      [inputImages, referencesSent],
  );
  const tags = usePromptTags({
      prompt,
      refs: tagRefs,
      fieldRef: promptRef,
      onPrompt: value => { markPromptEdit(); updateData({ prompt: value }); },
      userId,
      workspaceName,
  });


  // Current Config Values
  // Saved values are validated against the current model's option lists, so a
  // ratio/count persisted for Gemini is never sent to a Venice model that lacks it.
  // (utils/generatorSettings.ts, shared with the Claude connector: Venice resolution tiers "1K" | "2K" | "4K", GPT Image quality)
  const {
    aspectRatio, size: imageSize, resolution: imageResolution, quality: imageQuality, numberOfImages, steps,
  } = imageSettingsOf(data.imageGenConfig, modelDef);
  const selectedCustomWorkflowId = (data.imageGenConfig?.customWorkflowId as string) || '';
  const selectedCustomWorkflow = useMemo(
      () => customWorkflows.find((wf) => wf.filename === selectedCustomWorkflowId) || null,
      [customWorkflows, selectedCustomWorkflowId]
  );
  const modelSelectValue = currentModelId === 'comfyui-custom-workflow'
      ? (selectedCustomWorkflowId ? `custom:${selectedCustomWorkflowId}` : 'comfyui-custom-workflow')
      : currentModelId;
  const canGenerate = prompt.trim().length > 0 && (currentModelId !== 'comfyui-custom-workflow' || !!selectedCustomWorkflow);
  
  const status = data.generationStatus || 'idle';
  const cost = data.cost || 0;

  const updateData = (updates: Partial<NodeData>) => {
    if (isViewOnly) return;
    onDataChange({ ...data, ...updates });
  };

  /**
   * A generation's result or failure. It goes onto the node's live data, and when the user has
   * switched to another workspace meanwhile, it is held for this one instead of being lost.
   */
  const deliver = (updates: Partial<NodeData>) => {
    if (isViewOnly) return;
    if (onDataPatch && nodeId) { onDataPatch(nodeId, { ...updates, timestamp: Date.now() }); return; }
    updateData(updates);
  };

  const updateConfig = (key: string, value: string | number) => {
    const nextConfig: any = {
      ...data.imageGenConfig,
      model: currentModelId,
      aspectRatio,
      size: imageSize,
      resolution: imageResolution,
      quality: imageQuality,
      numberOfImages,
      steps,
    };

    switch (key) {
      case 'model':
        nextConfig.model = String(value);
        break;
      case 'resolution':
        nextConfig.resolution = String(value);
        break;
      case 'quality':
        nextConfig.quality = String(value);
        break;
      case 'aspectRatio':
        nextConfig.aspectRatio = String(value);
        break;
      case 'size':
        nextConfig.size = String(value);
        break;
      case 'numberOfImages':
        nextConfig.numberOfImages = Number(value);
        break;
      case 'steps':
        nextConfig.steps = Number(value);
        break;
      default:
        nextConfig[key] = value;
    }

    if (nextConfig.model !== 'comfyui-custom-workflow') {
      delete nextConfig.customWorkflowId;
    }

    // On a model switch, persist only values the new model supports.
    if (key === 'model') {
      const next = AI_MODELS[String(value)];
      if (next) {
        const kept = imageSettingsOf(nextConfig, next);
        nextConfig.aspectRatio = kept.aspectRatio;
        nextConfig.size = kept.size;
        nextConfig.resolution = kept.resolution;
        nextConfig.quality = kept.quality;
        nextConfig.numberOfImages = kept.numberOfImages;
      }
    }

    updateData({ imageGenConfig: nextConfig });
  };

  const setCustomWorkflowSelection = (workflowId: string) => {
    const nextConfig: any = {
        ...data.imageGenConfig,
        model: 'comfyui-custom-workflow',
        aspectRatio,
        numberOfImages,
        steps,
    };

    if (workflowId) {
        nextConfig.customWorkflowId = workflowId;
    } else {
        delete nextConfig.customWorkflowId;
    }

    updateData({ imageGenConfig: nextConfig });
  };

  const handleModelSelect = (value: string) => {
    if (value.startsWith('custom:')) {
        const workflowId = value.replace('custom:', '');
        setCustomWorkflowSelection(workflowId);
    } else if (value === 'comfyui-custom-workflow') {
        setCustomWorkflowSelection('');
    } else {
        updateConfig('model', value);
    }
  };

  const checkApiKey = async (): Promise<boolean> => {
    // ComfyUI models don't need API keys - they run locally
    if (modelDef.provider === 'comfyui') return true;
    
    // OpenAI models need OpenAI API key
    if (modelDef.provider === 'openai') {
      if (openAiApiKey) return true;
      setMissingKeyType('openai');
      setShowApiKeyPrompt(true);
      return false;
    }

    // Venice models (Nano Banana Pro, GPT Image via Venice) need a Venice API key
    if (modelDef.provider === 'venice') {
      if (veniceApiKey) return true;
      setMissingKeyType('venice');
      setShowApiKeyPrompt(true);
      return false;
    }
    
    // Google models need Google API key
    if (googleApiKey) return true;
    const win = window as any;
    if (win.aistudio && win.aistudio.hasSelectedApiKey) {
      const hasKey = await win.aistudio.hasSelectedApiKey();
      if (hasKey) return true;
    }
    setMissingKeyType('google');
    setShowApiKeyPrompt(true);
    return false;
  };

  const handleSelectKey = async () => {
    if (onRequestApiKey) {
        onRequestApiKey(missingKeyType);
        setShowApiKeyPrompt(false);
        return;
    }
    const win = window as any;
    if (win.aistudio && win.aistudio.openSelectKey) {
      await win.aistudio.openSelectKey();
      setShowApiKeyPrompt(false);
    }
  };

  /** Bytes of a connected image for the provider request; errors name the input. */
  const getBase64FromUrl = (url: string, label = 'the reference image') =>
      loadMediaAsBase64(url, { userId, ownerUserId, workspaceName, label, defaultMimeType: 'image/png' });

  const getAugmentedPrompt = () => {
      let finalPrompt = prompt;
      inputEntities.forEach(entity => {
          const name = entity.data.entityData?.name;
          const desc = entity.data.entityData?.description;
          if (name && desc && finalPrompt.toLowerCase().includes(name.toLowerCase())) {
              finalPrompt += ` (${name} visual appearance: ${desc})`;
          }
      });
      return finalPrompt;
  };

  const injectPromptIntoWorkflow = (workflowData: any, promptText: string): number => {
      if (!workflowData || !workflowData.nodes) return 0;
      const nodesSource = Array.isArray(workflowData.nodes)
          ? workflowData.nodes
          : Object.values(workflowData.nodes);

      let updatedCount = 0;
      nodesSource.forEach((node: any) => {
          if (!node) return;
          const title = (node.title || node.name || '').toLowerCase();
          if (!title || !title.includes('prompt') || title.includes('negative')) {
              return;
          }

          let updated = false;
          if (Array.isArray(node.widgets_values)) {
              const stringIndex = node.widgets_values.findIndex((value: unknown) => typeof value === 'string');
              if (stringIndex >= 0) {
                  node.widgets_values[stringIndex] = promptText;
                  updated = true;
              }
          }

          if (!updated && node.inputs && typeof node.inputs.text === 'string') {
              node.inputs.text = promptText;
              updated = true;
          }

          if (updated) {
              updatedCount++;
          }
      });

      return updatedCount;
  };

  // Shared tail: auto-save locally, upload to cloud, then mark the node completed.
  /** The file extension for a generated image's data URL: most are PNG, 4K ones from Venice WebP. */
  const imageExtension = (img: string): string => {
      const type = /^data:image\/([a-z]+)/i.exec(img)?.[1]?.toLowerCase();
      return type === 'jpeg' ? 'jpg' : type === 'webp' || type === 'jpg' ? type : 'png';
  };

  const persistGeneratedImages = async (uniqueImages: string[], finalCost: number, tag: string) => {
      // Generate shared timestamps for consistent filenames
      const timestamps = batchStamps(uniqueImages.length);

      // AUTO-SAVE locally if enabled
      try {
          if (onAutoSaveFile) {
              for (let i = 0; i < uniqueImages.length; i++) {
                  const img = uniqueImages[i];
                  if (img.startsWith('data:')) {
                      const ext = imageExtension(img);
                      const filename = `generated_images_${timestamps[i]}.${ext}`;
                      await onAutoSaveFile(img, filename, 'Generated_Images', ext);
                  }
              }
          }
      } catch (autoSaveError) {
          console.warn(`${tag}: Auto-save failed:`, autoSaveError);
      }

      // UPLOAD TO CLOUD
      let uploadedImages = uniqueImages;
      if (userId) {
          onAutoSaveStatus?.('cloud', `Uploading ${uniqueImages.length} image${uniqueImages.length > 1 ? 's' : ''} to cloud...`);
          try {
              uploadedImages = await Promise.all(
                  uniqueImages.map(async (img, idx) => {
                      if (img.startsWith('data:') && !isFileReference(img)) {
                          const fileRef = await uploadWithDeduplication(userId, img, {
                              category: 'Generated_Images',
                              workspaceName,
                              displayNamePrefix: 'generated_images',
                              extension: imageExtension(img),
                              nodeId,
                              timestamp: timestamps[idx],
                          });
                          if (fileRef.startsWith('file://')) {
                              const localPath = `Generated_Images/generated_images_${timestamps[idx]}.${imageExtension(img)}`;
                              registerLocalFileMapping(fileRef, localPath);
                          }
                          return fileRef;
                      }
                      return img;
                  })
              );
              onAutoSaveStatus?.('cloud', `Uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''} to cloud`);
          } catch (uploadError) {
              console.warn(`${tag}: Immediate upload failed, keeping base64:`, uploadError);
          }
      }

      deliver({
          generatedImageUri: uploadedImages[0],
          generatedImages: uploadedImages,
          generationStatus: 'completed',
          timestamp: Date.now(),
          cost: finalCost,
      });
  };

  const generateImage = async () => {
    if (isViewOnly) return;
    console.log('🎨 Starting image generation...', { hasAutoSaveCallback: !!onAutoSaveStatus });
    if (!prompt.trim()) return;
    const hasKey = await checkApiKey();
    if (!hasKey) return;
    if (currentModelId === 'comfyui-custom-workflow' && !selectedCustomWorkflow) {
        updateData({ generationStatus: 'error', errorMessage: 'Select a custom workflow before generating.' });
        return;
    }
    
    // Reset cancellation state
    isCancelledRef.current = false;

    setIsGenerating(true);
    updateData({ generationStatus: 'generating', errorMessage: undefined });

    try {
      const keyToUse = googleApiKey;
      
      // Gemini 2.5 Flash uses generateContent, Gemini 3 Pro Image uses generateContent with image output
      const isFlashModel = currentModelId === 'gemini-2.5-flash-image';
      const isGemini3ProImage = currentModelId === 'gemini-3-pro-image-preview';
      
      if (isFlashModel) {
          // Gemini 2.5 Flash Image - use generateContent endpoint with image_config
          const url = `https://generativelanguage.googleapis.com/v1beta/models/${currentModelId}:generateContent?key=${keyToUse}`;
          
          // Build prompt
          let promptText = getAugmentedPrompt();
          
          // Construct parts - text first, then reference images
          const parts: any[] = [{ text: promptText }];
          
          // Add reference images if available
          if (inputImages.length > 0 && modelDef.supportsImageReference) {
              for (const img of inputImages.slice(0, modelDef.maxReferenceImages || 1)) {
                 try {
                    const { data, mimeType } = await getBase64FromUrl(img.url, img.label || 'the reference image');
                    parts.push({ inlineData: { mimeType, data } });
                 } catch (e) {
                    console.error("Failed to load reference image", img.url, e);
                    throw e instanceof Error ? e : new Error(`Failed to load reference image: ${img.label || 'Image'}.`);
                 }
              }
              // Append reference instruction
              parts[0].text += `\n\nUse the provided reference image for visual style and subject matter.`;
          }
          
          const runSingleGeneration = async () => {
              const response = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      contents: [{ parts }],
                      generationConfig: { 
                          responseModalities: ["TEXT", "IMAGE"],
                          // image_config nested inside generationConfig per API spec
                          imageConfig: {
                              aspectRatio: aspectRatio || '1:1'
                          }
                      }
                  })
              });

              const payload = await response.json();
              
              if (!response.ok) {
                  const errMsg = payload?.error?.message || response.statusText;
                  throw new Error(`Google API Error: ${errMsg}`);
              }

              if (payload.candidates && payload.candidates.length > 0) {
                  for (const candidate of payload.candidates) {
                      const part = candidate.content?.parts?.find((p: any) => p.inlineData);
                      if (part && part.inlineData) {
                          return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                      }
                  }
              }
              return null;
          };

          const promises = Array.from({ length: numberOfImages }, () => runSingleGeneration());
          const results = await Promise.all(promises);
          const validImages = results.filter(img => img !== null) as string[];
          
          // Deduplicate identical images (Gemini sometimes returns same image for multiple requests)
          const uniqueImages = [...new Set(validImages)];

          if (uniqueImages.length > 0) {
            const finalCost = calculateCost(currentModelId, {}) * validImages.length; // Cost for all attempts
            
            // Generate shared timestamps for consistent filenames between local and cloud
            const timestamps = batchStamps(uniqueImages.length);
            
            // AUTO-SAVE (both local and Google Drive if enabled)
            try {
                if (onAutoSaveFile) {
                    for (let i = 0; i < uniqueImages.length; i++) {
                        const img = uniqueImages[i];
                        if (img.startsWith('data:')) {
                            const filename = `generated_images_${timestamps[i]}.png`;
                            await onAutoSaveFile(img, filename, 'Generated_Images', 'png');
                        }
                    }
                }
            } catch (autoSaveError) {
                console.warn('Flash model: Auto-save failed:', autoSaveError);
            }
            
            // UPLOAD TO CLOUD - convert base64 to file:// references (using same timestamps)
            let uploadedImages = uniqueImages;
            if (userId) {
                onAutoSaveStatus?.('cloud', `Uploading ${uniqueImages.length} image${uniqueImages.length > 1 ? 's' : ''} to cloud...`);
                try {
                    uploadedImages = await Promise.all(
                        uniqueImages.map(async (img, idx) => {
                            if (img.startsWith('data:') && !isFileReference(img)) {
                                const fileRef = await uploadWithDeduplication(userId, img, {
                                    category: 'Generated_Images',
                                    workspaceName,
                                    displayNamePrefix: 'generated_images', // Match local filename format
                                    extension: 'png',
                                    nodeId,
                                    timestamp: timestamps[idx], // Use same timestamp as local save
                                });
                                // Register mapping for offline resolution
                                if (fileRef.startsWith('file://')) {
                                    const localPath = `Generated_Images/generated_images_${timestamps[idx]}.png`;
                                    registerLocalFileMapping(fileRef, localPath);
                                }
                                return fileRef;
                            }
                            return img;
                        })
                    );
                    onAutoSaveStatus?.('cloud', `Uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''} to cloud`);
                } catch (uploadError) {
                    console.warn('Immediate upload failed, keeping base64:', uploadError);
                }
            }
            
            deliver({ 
                generatedImageUri: uploadedImages[0],
                generatedImages: uploadedImages,
                generationStatus: 'completed',
                timestamp: Date.now(),
                cost: finalCost
            });
          } else {
              throw new Error('No image data returned. The model might have generated text instead.');
          }
          
      } else if (isGemini3ProImage) {
          // Gemini 3 Pro Image - use generateContent endpoint with image output
          const url = `https://generativelanguage.googleapis.com/v1beta/models/${currentModelId}:generateContent?key=${keyToUse}`;
          
          // Build prompt
          let promptText = getAugmentedPrompt();
          
          // Construct parts - text first, then reference images
          const parts: any[] = [{ text: promptText }];
          
          // Add reference images if available
          if (inputImages.length > 0 && modelDef.supportsImageReference) {
              for (const img of inputImages.slice(0, modelDef.maxReferenceImages || 1)) {
                 try {
                    const { data, mimeType } = await getBase64FromUrl(img.url, img.label || 'the reference image');
                    parts.push({ inlineData: { mimeType, data } });
                 } catch (e) {
                    console.error("Failed to load reference image", img.url, e);
                    throw e instanceof Error ? e : new Error(`Failed to load reference image: ${img.label || 'Image'}.`);
                 }
              }
              // Append reference instruction
              parts[0].text += `\n\nUse the provided reference image(s) for visual style and subject matter.`;
          }

          const runSingleGeneration = async () => {
              const response = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                      contents: [{ parts }],
                      generationConfig: { 
                          responseModalities: ["IMAGE"],
                          imageConfig: {
                              aspectRatio: aspectRatio || '1:1'
                          }
                      }
                  })
              });

              const payload = await response.json();
              
              if (!response.ok) {
                  const errMsg = payload?.error?.message || response.statusText;
                  throw new Error(`Google API Error: ${errMsg}`);
              }

              if (payload.candidates && payload.candidates.length > 0) {
                  for (const candidate of payload.candidates) {
                      const part = candidate.content?.parts?.find((p: any) => p.inlineData);
                      if (part && part.inlineData) {
                          return `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
                      }
                  }
              }
              return null;
          };

          const promises = Array.from({ length: numberOfImages }, () => runSingleGeneration());
          const results = await Promise.all(promises);
          const validImages = results.filter(img => img !== null) as string[];
          
          // Deduplicate identical images (Gemini sometimes returns same image for multiple requests)
          const uniqueImages = [...new Set(validImages)];
          console.log('🎨 Generated', uniqueImages.length, 'unique images');

          if (uniqueImages.length > 0) {
            const finalCost = calculateCost(currentModelId, {}) * validImages.length; // Cost for all attempts
            
            // Generate shared timestamps for consistent filenames between local and cloud
            const timestamps = batchStamps(uniqueImages.length);
            
            // AUTO-SAVE (both local and Google Drive if enabled)
            try {
                if (onAutoSaveFile) {
                    for (let i = 0; i < uniqueImages.length; i++) {
                        const img = uniqueImages[i];
                        if (img.startsWith('data:')) {
                            const filename = `generated_images_${timestamps[i]}.png`;
                            await onAutoSaveFile(img, filename, 'Generated_Images', 'png');
                        }
                    }
                }
            } catch (autoSaveError) {
                console.warn('Auto-save failed:', autoSaveError);
            }
            
            // UPLOAD TO CLOUD - convert base64 to file:// references (using same timestamps)
            let uploadedImages = uniqueImages;
            if (userId) {
                onAutoSaveStatus?.('cloud', `Uploading ${uniqueImages.length} image${uniqueImages.length > 1 ? 's' : ''} to cloud...`);
                try {
                    uploadedImages = await Promise.all(
                        uniqueImages.map(async (img, idx) => {
                            if (img.startsWith('data:') && !isFileReference(img)) {
                                const fileRef = await uploadWithDeduplication(userId, img, {
                                    category: 'Generated_Images',
                                    workspaceName,
                                    displayNamePrefix: 'generated_images', // Match local filename format
                                    extension: 'png',
                                    nodeId,
                                    timestamp: timestamps[idx], // Use same timestamp as local save
                                });
                                // Register mapping for offline resolution
                                if (fileRef.startsWith('file://')) {
                                    const localPath = `Generated_Images/generated_images_${timestamps[idx]}.png`;
                                    registerLocalFileMapping(fileRef, localPath);
                                }
                                return fileRef;
                            }
                            return img;
                        })
                    );
                    onAutoSaveStatus?.('cloud', `Uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''} to cloud`);
                } catch (uploadError) {
                    console.warn('Immediate upload failed, keeping base64:', uploadError);
                }
            }
            
            deliver({ 
                generatedImageUri: uploadedImages[0],
                generatedImages: uploadedImages,
                generationStatus: 'completed',
                timestamp: Date.now(),
                cost: finalCost
            });
          } else {
              throw new Error('No image data returned. Safety filters may have blocked the content.');
          }
      } else if (modelDef.provider === 'comfyui') {
          // Local ComfyUI generation
          const client = getComfyUIClient();
          
          // Check if ComfyUI is connected
          const connected = await client.ping();
          if (!connected) {
              throw new Error('ComfyUI is not connected. Please check Settings → Local models and ensure ComfyUI is running.');
          }
          const useCustomWorkflow = currentModelId === 'comfyui-custom-workflow';
          let workflow: any;
          
          if (useCustomWorkflow) {
              if (!selectedCustomWorkflow) {
                  throw new Error('Select a custom workflow before generating.');
              }
              
              try {
                  workflow = JSON.parse(JSON.stringify(selectedCustomWorkflow.json));
              } catch (e) {
                  throw new Error(`Unable to parse workflow "${selectedCustomWorkflow.name}". Make sure the JSON file is valid.`);
              }
              
              const injections = injectPromptIntoWorkflow(workflow, getAugmentedPrompt());
              if (injections === 0) {
                  throw new Error(`"${selectedCustomWorkflow.name}" does not include a prompt node. Rename your prompt node to include the word "Prompt".`);
              }
              
              console.log(`Injected prompt into ${injections} node(s) for workflow ${selectedCustomWorkflow.filename}`);
          } else {
              // Get checkpoint from localStorage or detect based on model pattern
              const availableCheckpoints = await client.getCheckpoints();
              let checkpoint = localStorage.getItem('comfyui_checkpoint');
              
              if (modelDef.checkpointPattern) {
                  const pattern = new RegExp(modelDef.checkpointPattern, 'i');
                  const matchedCheckpoint = availableCheckpoints.find(cp => pattern.test(cp));
                  if (matchedCheckpoint) {
                      checkpoint = matchedCheckpoint;
                  } else {
                      if (modelDef.checkpointPattern.includes('flux')) {
                          const fluxFallback = availableCheckpoints.find(cp => /flux/i.test(cp));
                          if (fluxFallback) {
                              console.warn(`Specific FLUX model not found (${modelDef.checkpointPattern}), falling back to: ${fluxFallback}`);
                              checkpoint = fluxFallback;
                          } else {
                              const unets = await client.getUnets();
                              const matchedUnet = unets.find(u => pattern.test(u)) || unets.find(u => /flux/i.test(u));
                              if (matchedUnet) {
                                  console.log('Found FLUX UNET:', matchedUnet);
                                  checkpoint = `UNET:${matchedUnet}`;
                              } else {
                                  throw new Error(`No FLUX model found in Checkpoints or UNETs. Please download a FLUX checkpoint (e.g. flux1-schnell.safetensors) to your ComfyUI models folder.`);
                              }
                          }
                      } else if (modelDef.checkpointPattern.includes('xl')) {
                          const xlFallback = availableCheckpoints.find(cp => /xl|sdxl/i.test(cp));
                          if (xlFallback) {
                              checkpoint = xlFallback;
                          } else {
                              throw new Error(`No SDXL model found. Please download an SDXL checkpoint.`);
                          }
                      } else {
                          throw new Error(`Model not found matching pattern: ${modelDef.checkpointPattern}`);
                      }
                  }
              }
              
              if (!checkpoint) {
                  if (availableCheckpoints.length === 0) {
                      throw new Error('No model checkpoints found in ComfyUI. Please add models to your ComfyUI/models/checkpoints folder.');
                  }
                  console.warn('Using default checkpoint as no specific match was found:', availableCheckpoints[0]);
                  checkpoint = availableCheckpoints[0];
              }
              
              const { width, height } = aspectRatioToSize(aspectRatio, 1024);
              const useUnet = checkpoint.startsWith('UNET:');
              const isFlux = useUnet || currentModelId.includes('flux') || checkpoint.toLowerCase().includes('flux');
              
              if (useUnet) {
                  const unetName = checkpoint.replace('UNET:', '');
                  const clips = await client.getClips();
                  const vaes = await client.getVaes();
                  
                  const t5 = clips.find(c => /t5|xxl/i.test(c)) || clips[0];
                  const clipL = clips.find(c => /clip_l|vit-l/i.test(c)) || clips[1] || clips[0];
                  const vae = vaes.find(v => /ae|flux/i.test(v)) || vaes[0];
                  
                  if (!t5 || !clipL || !vae) {
                      throw new Error('Missing CLIP or VAE models for FLUX UNET workflow. Please ensure you have t5xxl, clip_l, and ae.safetensors in your ComfyUI models.');
                  }
                  
                  workflow = buildFluxUnetWorkflow({
                      unet: unetName,
                      clip1: t5,
                      clip2: clipL,
                      vae: vae,
                      prompt: getAugmentedPrompt(),
                      width,
                      height,
                      steps: steps,
                      guidance: 3.5,
                  });
              } else if (inputImages.length > 0 && modelDef.supportsImageReference) {
                  onAutoSaveStatus?.('local', 'Uploading reference image to ComfyUI...');
                  const { data, mimeType } = await getBase64FromUrl(inputImages[0].url, inputImages[0].label || 'the reference image');
                  const byteString = atob(data);
                  const ab = new ArrayBuffer(byteString.length);
                  const ia = new Uint8Array(ab);
                  for (let i = 0; i < byteString.length; i++) {
                      ia[i] = byteString.charCodeAt(i);
                  }
                  const blob = new Blob([ab], { type: mimeType });
                  
                  const uploadedImage = await client.uploadImage(blob, 'ref_image_' + Date.now() + '.png');
                  
                  if (isFlux) {
                      console.warn('FLUX img2img not yet supported, falling back to txt2img');
                      workflow = buildFluxWorkflow({
                          checkpoint,
                          prompt: getAugmentedPrompt(),
                          width,
                          height,
                          steps: steps,
                          guidance: 3.5,
                      });
                  } else {
                      workflow = buildImg2ImgWorkflow({
                          checkpoint,
                          imageName: uploadedImage.name,
                          prompt: getAugmentedPrompt(),
                          negativePrompt: 'blurry, low quality, distorted',
                          steps: steps,
                          cfg: 7,
                          denoise: 0.6,
                      });
                  }
              } else {
                  workflow = isFlux 
                      ? buildFluxWorkflow({
                          checkpoint,
                          prompt: getAugmentedPrompt(),
                          width,
                          height,
                          steps: steps,
                          guidance: 3.5,
                      })
                      : buildTxt2ImgWorkflow({
                          checkpoint,
                          prompt: getAugmentedPrompt(),
                          negativePrompt: 'blurry, low quality, distorted',
                          width,
                          height,
                          steps: steps,
                          cfg: 7,
                      });
              }
          }
          
          onAutoSaveStatus?.('local', useCustomWorkflow ? `Running ${selectedCustomWorkflow?.name || 'custom workflow'}...` : 'Generating with local model...');
          
          // Convert custom workflows from UI format to API format
          const workflowToQueue = useCustomWorkflow ? convertWorkflowToApi(workflow) : workflow;
          
          // Queue the generation
          const queued = await client.queuePrompt(workflowToQueue);
          console.log('ComfyUI queued prompt:', queued.prompt_id);
          
          // Wait for completion
          const result = await client.waitForCompletion(queued.prompt_id, (progress) => {
              if (progress.status === 'running') {
                  onAutoSaveStatus?.('local', 'Generating image...');
              }
          });
          
          // Find the LAST output with images (ComfyUI processes in dependency order)
          // The last SaveImage node to execute is the final stage of any workflow
          const outputs = Object.values(result.outputs);
          const imageOutputs = outputs.filter((o: any) => o.images && o.images.length > 0);
          const imageOutput = imageOutputs.length > 0 ? imageOutputs[imageOutputs.length - 1] : null;
          
          if (!imageOutput || !imageOutput.images[0]) {
              throw new Error('No image output from ComfyUI');
          }
          
          // Download the generated image
          const imageInfo = imageOutput.images[0];
          const imageBlob = await client.getImage(imageInfo.filename, imageInfo.subfolder, imageInfo.type);
          
          // Convert blob to data URL
          const reader = new FileReader();
          const dataUrl = await new Promise<string>((resolve) => {
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(imageBlob);
          });
          
          // Auto-save locally and/or to Google Drive if enabled
          const timestamp = Date.now();
          const filename = `generated_images_${timestamp}.png`;
          
          try {
              if (onAutoSaveFile) {
                  await onAutoSaveFile(dataUrl, filename, 'Generated_Images', 'png');
              }
          } catch (autoSaveError) {
              console.warn('ComfyUI: Auto-save failed:', autoSaveError);
          }
          
          // Upload to cloud if user is logged in
          let finalImageUri = dataUrl;
          if (userId) {
              onAutoSaveStatus?.('cloud', 'Uploading to cloud...');
              try {
                  const fileRef = await uploadWithDeduplication(userId, dataUrl, {
                      category: 'Generated_Images',
                      workspaceName,
                      displayNamePrefix: 'generated_images',
                      extension: 'png',
                      nodeId,
                      timestamp,
                  });
                  if (fileRef.startsWith('file://')) {
                      const localPath = `Generated_Images/generated_images_${timestamp}.png`;
                      registerLocalFileMapping(fileRef, localPath);
                  }
                  finalImageUri = fileRef;
                  onAutoSaveStatus?.('cloud', 'Uploaded to cloud');
              } catch (uploadError) {
                  console.warn('ComfyUI: Cloud upload failed:', uploadError);
              }
          }
          
          onAutoSaveStatus?.('local', 'Generation complete!');
          
          deliver({
              generatedImageUri: finalImageUri,
              generatedImages: [finalImageUri],
              generationStatus: 'completed',
              timestamp: Date.now(),
              cost: 0, // Free - local generation
          });
      } else if (modelDef.provider === 'openai') {
          // OpenAI GPT Image generation
          if (!openAiApiKey) {
              throw new Error('OpenAI API key is required for this model.');
          }

          const apiModelId = modelDef.apiModelId || currentModelId;
          const quality = modelDef.defaultQuality || 'medium';
          const hasInputImages = inputImages.length > 0 && modelDef.supportsImageReference;

          const runSingleOpenAIGeneration = async (): Promise<string | null> => {
              // Prepare request body for the Netlify proxy function
              const requestBody: {
                  apiKey: string;
                  model: string;
                  prompt: string;
                  size: string;
                  quality: string;
                  n: number;
                  outputFormat: string;
                  background: string;
                  images?: Array<{ data: string; mimeType: string; filename: string }>;
              } = {
                  apiKey: openAiApiKey!,
                  model: apiModelId,
                  prompt: getAugmentedPrompt(),
                  size: imageSize,
                  quality: quality,
                  n: 1,
                  outputFormat: 'png',
                  background: 'auto',
              };

              // Add reference images if available (for image-to-image generation)
              if (hasInputImages) {
                  const maxImages = modelDef.maxReferenceImages || 16;
                  const imageData: Array<{ data: string; mimeType: string; filename: string }> = [];
                  
                  for (const img of inputImages.slice(0, maxImages)) {
                      try {
                          const { data, mimeType } = await getBase64FromUrl(img.url, img.label || 'the reference image');
                          const ext = mimeType.includes('png') ? 'png' : mimeType.includes('webp') ? 'webp' : 'jpg';
                          imageData.push({
                              data, // base64 string without prefix
                              mimeType,
                              filename: `reference.${ext}`,
                          });
                      } catch (e) {
                          console.error("Failed to load reference image for OpenAI:", img.url, e);
                          throw e instanceof Error ? e : new Error(`Failed to load reference image: ${img.label || 'Image'}.`);
                      }
                  }
                  
                  if (imageData.length > 0) {
                      requestBody.images = imageData;
                  }
              }

              // Call OpenAI API directly from browser to avoid Netlify timeout limits
              // The API key is already client-side, so no security concern
              let response: Response;
              
              if (requestBody.images && requestBody.images.length > 0) {
                  // Use FormData for image edits endpoint
                  const formData = new FormData();
                  formData.append('model', requestBody.model);
                  formData.append('prompt', requestBody.prompt);
                  formData.append('size', requestBody.size);
                  formData.append('quality', requestBody.quality);
                  formData.append('n', requestBody.n.toString());
                  formData.append('output_format', requestBody.outputFormat);
                  formData.append('background', requestBody.background);
                  
                  for (const img of requestBody.images) {
                      // Convert base64 to blob
                      const binaryStr = atob(img.data);
                      const bytes = new Uint8Array(binaryStr.length);
                      for (let i = 0; i < binaryStr.length; i++) {
                          bytes[i] = binaryStr.charCodeAt(i);
                      }
                      const blob = new Blob([bytes], { type: img.mimeType });
                      formData.append('image[]', blob, img.filename);
                  }
                  
                  response = await fetch('https://api.openai.com/v1/images/edits', {
                      method: 'POST',
                      headers: {
                          'Authorization': `Bearer ${requestBody.apiKey}`,
                      },
                      body: formData,
                  });
              } else {
                  // Use JSON for text-to-image generations endpoint
                  response = await fetch('https://api.openai.com/v1/images/generations', {
                      method: 'POST',
                      headers: {
                          'Authorization': `Bearer ${requestBody.apiKey}`,
                          'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                          model: requestBody.model,
                          prompt: requestBody.prompt,
                          n: requestBody.n,
                          size: requestBody.size,
                          quality: requestBody.quality,
                          output_format: requestBody.outputFormat,
                          background: requestBody.background,
                      }),
                  });
              }

              // Get response text first to handle empty responses
              const responseText = await response.text();
              
              if (!responseText) {
                  throw new Error('Empty response from OpenAI API.');
              }

              let payload;
              try {
                  payload = JSON.parse(responseText);
              } catch (e) {
                  console.error('Failed to parse OpenAI response:', responseText.substring(0, 200));
                  throw new Error(`Invalid response from OpenAI: ${responseText.substring(0, 100)}`);
              }

              if (!response.ok) {
                  const errMsg = payload?.error || payload?.message || response.statusText;
                  throw new Error(`OpenAI API Error: ${errMsg}`);
              }

              // OpenAI GPT Image models return base64 directly
              if (payload.data && payload.data.length > 0) {
                  const imageData = payload.data[0];
                  if (imageData.b64_json) {
                      return `data:image/png;base64,${imageData.b64_json}`;
                  } else if (imageData.url) {
                      // Fallback for DALL-E models that return URLs
                      return imageData.url;
                  }
              }
              return null;
          };

          // Generate multiple images in parallel
          const promises = Array.from({ length: numberOfImages }, () => runSingleOpenAIGeneration());
          const results = await Promise.all(promises);
          const validImages = results.filter(img => img !== null) as string[];

          // Deduplicate identical images
          const uniqueImages = [...new Set(validImages)];
          console.log('🎨 OpenAI generated', uniqueImages.length, 'unique images', hasInputImages ? '(with image reference)' : '(text-only)');

          if (uniqueImages.length > 0) {
              const finalCost = calculateCost(currentModelId, {}) * validImages.length;

              // Generate shared timestamps for consistent filenames
              const timestamps = batchStamps(uniqueImages.length);

              // AUTO-SAVE locally if enabled
              try {
                  if (onAutoSaveFile) {
                      for (let i = 0; i < uniqueImages.length; i++) {
                          const img = uniqueImages[i];
                          if (img.startsWith('data:')) {
                              const filename = `generated_images_${timestamps[i]}.png`;
                              await onAutoSaveFile(img, filename, 'Generated_Images', 'png');
                          }
                      }
                  }
              } catch (autoSaveError) {
                  console.warn('OpenAI: Auto-save failed:', autoSaveError);
              }

              // UPLOAD TO CLOUD
              let uploadedImages = uniqueImages;
              if (userId) {
                  onAutoSaveStatus?.('cloud', `Uploading ${uniqueImages.length} image${uniqueImages.length > 1 ? 's' : ''} to cloud...`);
                  try {
                      uploadedImages = await Promise.all(
                          uniqueImages.map(async (img, idx) => {
                              if (img.startsWith('data:') && !isFileReference(img)) {
                                  const fileRef = await uploadWithDeduplication(userId, img, {
                                      category: 'Generated_Images',
                                      workspaceName,
                                      displayNamePrefix: 'generated_images',
                                      extension: 'png',
                                      nodeId,
                                      timestamp: timestamps[idx],
                                  });
                                  if (fileRef.startsWith('file://')) {
                                      const localPath = `Generated_Images/generated_images_${timestamps[idx]}.png`;
                                      registerLocalFileMapping(fileRef, localPath);
                                  }
                                  return fileRef;
                              }
                              return img;
                          })
                      );
                      onAutoSaveStatus?.('cloud', `Uploaded ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''} to cloud`);
                  } catch (uploadError) {
                      console.warn('OpenAI: Immediate upload failed, keeping base64:', uploadError);
                  }
              }

              deliver({
                  generatedImageUri: uploadedImages[0],
                  generatedImages: uploadedImages,
                  generationStatus: 'completed',
                  timestamp: Date.now(),
                  cost: finalCost,
              });
          } else {
              throw new Error('No image data returned from OpenAI. The request may have been blocked by content filters.');
          }
      } else if (modelDef.provider === 'venice') {
          // Venice: Nano Banana Pro / GPT Image. Text-to-image via /image/generate;
          // with connected images the *-edit model combines them via /image/multi-edit.
          if (!veniceApiKey) {
              throw new Error('Venice API key is required for this model.');
          }

          const promptText = getAugmentedPrompt();
          const limit = modelDef.promptCharacterLimit;
          if (limit && promptText.length > limit) {
              throw new Error(`Prompt is too long for ${modelDef.label} (${promptText.length}/${limit} characters).`);
          }

          const wantsResolution = !!modelDef.supportedResolutions;
          const wantsQuality = !!modelDef.supportedQualities;
          // A 4K PNG comes back as 40 MB or more of text; WebP is a fraction of that
          const format = wantsResolution && imageResolution === '4K' ? 'webp' as const : undefined;
          const count = Math.max(1, Math.min(numberOfImages, modelDef.maxGeneratedImages || 4));
          const useEdit = inputImages.length > 0 && modelDef.supportsImageReference && !!modelDef.editApiModelId;
          // Whether Venice blurs adult content: on unless turned off in Settings
          const safeMode = veniceSafeModeOn();

          let results: string[] = [];
          if (useEdit) {
              const refs: string[] = [];
              for (const img of inputImages.slice(0, modelDef.maxReferenceImages || 6)) {
                  try {
                      const { data, mimeType } = await getBase64FromUrl(img.url, img.label || 'the reference image');
                      refs.push(`data:${mimeType};base64,${data}`);
                  } catch (e) {
                      console.error('Failed to load reference image for Venice:', img.url, e);
                      throw e instanceof Error ? e : new Error(`Failed to load reference image: ${img.label || 'Image'}.`);
                  }
              }
              // All pictures go in one request, which Venice refuses at 32 MB. Large ones are sent as a
              // reference needs them (as large as the result, a JPEG), so the request uploads quickly
              const resultLongSide = wantsResolution ? ({ '1K': 1024, '2K': 2048, '4K': 4096 } as Record<string, number>)[imageResolution] ?? 2048 : 2048;
              const fittedRefs = await fitPicturesToRequest(refs, requestBytes(promptText), undefined, () => onAutoSaveStatus?.('local', 'Making large pictures lighter to send...'), referenceStep(resultLongSide));
              onAutoSaveStatus?.('local', `Editing with ${modelDef.label}...`);
              const editOnce = () => veniceEditImage({
                  apiKey: veniceApiKey,
                  model: modelDef.editApiModelId!,
                  prompt: promptText,
                  images: fittedRefs,
                  aspectRatio,
                  resolution: wantsResolution ? imageResolution : undefined,
                  quality: wantsQuality ? imageQuality : undefined,
                  format,
                  safeMode,
              });
              // Each picture asked for is a request of its own with every reference in it. Together they
              // can be more than a slow connection uploads before Venice stops waiting: then one after another
              const uploadBytes = fittedRefs.reduce((sum, picture) => sum + picture.length, 0) * count;
              if (count > 1 && uploadBytes > PARALLEL_UPLOAD_BYTES) {
                  results = [];
                  for (let i = 0; i < count; i++) results.push(await editOnce());
              } else {
                  results = await Promise.all(Array.from({ length: count }, editOnce));
              }
          } else {
              results = await veniceGenerateImage({
                  apiKey: veniceApiKey,
                  model: modelDef.apiModelId || currentModelId,
                  prompt: promptText,
                  aspectRatio,
                  resolution: wantsResolution ? imageResolution : undefined,
                  quality: wantsQuality ? imageQuality : undefined,
                  variants: count,
                  format,
                  safeMode,
              });
          }

          void refreshVeniceBalance(veniceApiKey, true); // what these cost has been taken
          const uniqueImages = [...new Set(results.filter(Boolean))];
          console.log('🎨 Venice generated', uniqueImages.length, 'image(s)', useEdit ? '(edit with references)' : '(text-to-image)');
          if (uniqueImages.length === 0) {
              throw new Error('No image data returned from Venice. The request may have been blocked by content filters.');
          }

          const finalCost = calculateCost(currentModelId, {
              resolution: imageResolution,
              quality: imageQuality,
              edit: useEdit,
              inputImages: useEdit ? Math.min(inputImages.length, modelDef.maxReferenceImages || 6) : 0,
          }) * uniqueImages.length;
          await persistGeneratedImages(uniqueImages, finalCost, 'Venice');
      } else {
          throw new Error(`Model ${currentModelId} is not supported for image generation.`);
      }

    } catch (err: any) {
      if (isCancelledRef.current) {
          console.log('Generation cancelled, suppressing error');
          return;
      }
      console.error(err);
      let errorMsg = err.message || 'Failed to generate image.';
      deliver({ generationStatus: 'error', errorMessage: errorMsg });
    } finally {
      setIsGenerating(false);
    }
  };

  // Per-image price for the request that would actually be sent (edit model when
  // references are connected), multiplied by the requested count.
  const willEdit = inputImages.length > 0 && !!modelDef.editApiModelId;
  const estimatedCost = useMemo(
      () => calculateCost(currentModelId, {
          resolution: imageResolution,
          quality: imageQuality,
          edit: willEdit,
          inputImages: willEdit ? Math.min(inputImages.length, modelDef.maxReferenceImages || 6) : 0,
      }) * numberOfImages,
      [currentModelId, imageResolution, imageQuality, willEdit, inputImages.length, modelDef.maxReferenceImages, numberOfImages]
  );
  const modelsList = useMemo(() => getImageModels(), []);
  const builtInModels = useMemo(() => modelsList.filter(m => m.id !== 'comfyui-custom-workflow' && m.provider !== 'venice'), [modelsList]);
  const veniceModels = useMemo(() => modelsList.filter(m => m.provider === 'venice'), [modelsList]);
  // The model menu, by provider; then the custom ComfyUI workflows
  const modelOptions = useMemo<ChipOption<string>[]>(() => [
    ...builtInModels.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: PROVIDER_NAMES[m.provider] ?? m.provider })),
    ...veniceModels.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: 'Venice' })),
    ...(customWorkflows.length > 0
      ? customWorkflows.map(workflow => ({ value: `custom:${workflow.filename}`, label: workflow.name, group: 'Custom workflows' }))
      : [{ value: 'comfyui-custom-workflow', label: 'No workflows found', group: 'Custom workflows', disabled: true }]),
  ], [builtInModels, veniceModels, customWorkflows]);
  // Pictures from the workspace's Reference nodes, opened from the Image refs tile
  const refPicker = useAddReferences({
    kinds: canPickReferences ? ['image'] : [],
    picked: data.pickedReferences,
    connectedUrls: inputImages.filter(img => !isPickedInputId(img.id)).map(img => img.url),
    getChoices: getReferenceChoices,
    onChange: onPickedReferencesChange,
    userId,
    ownerUserId,
    workspaceName,
    sharing: referenceSharing,
  });

  // Get raw image references (could be file:// refs or direct URLs)
  const rawGeneratedImages = data.generatedImages && data.generatedImages.length > 0 
      ? data.generatedImages 
      : (data.generatedImageUri ? [data.generatedImageUri] : []);
  
  // Track the timestamp to detect when new images are generated
  const imageTimestamp = data.timestamp || 0;
  
  // Resolve file:// references to actual URLs (local-first if workspace auto-save enabled)
  const { urls: resolvedImageUrls, isLoading: isResolvingUrls } = useFileUrls(userId, rawGeneratedImages, workspaceName, ownerUserId);
  
  // Check if we're still resolving (either hook is loading, or resolved URLs don't match raw count)
  const isStillResolving = isResolvingUrls || 
      (rawGeneratedImages.length > 0 && resolvedImageUrls.length !== rawGeneratedImages.length) ||
      (rawGeneratedImages.some((raw: string) => isFileReference(raw)) && resolvedImageUrls.every((url: string | null) => !url));
  
  // Use resolved URLs for display, but only if they're valid for current images.
  // Each shown picture keeps its stored reference beside it: that is what the node's data holds,
  // never the address it is shown from (which only lives for this session)
  const shownImages = useMemo<{ url: string; stored: string }[]>(() => {
      if (isStillResolving) {
          // While resolving, only show non-file:// URLs (like data: URLs)
          return rawGeneratedImages
              .filter((url: string) => url && !isFileReference(url))
              .map((url: string) => ({ url, stored: url }));
      }
      return resolvedImageUrls
          .map((url, i) => ({ url: url || rawGeneratedImages[i], stored: rawGeneratedImages[i] }))
          .filter((image): image is { url: string; stored: string } => !!image.url);
  }, [resolvedImageUrls, rawGeneratedImages, isStillResolving]);
  const generatedImages = useMemo(() => shownImages.map(image => image.url), [shownImages]);

  const currentImage = generatedImages[selectedImageIndex] || generatedImages[0];

  const handleSelectImage = (index: number) => {
      setSelectedImageIndex(index);
      const stored = shownImages[index]?.stored;
      if (stored) {
          updateData({ generatedImageUri: stored });
      }
  };

  return (
    <div ref={fitRef} className="w-full h-full flex flex-col bg-white overflow-hidden">
      {/* PREVIEW AREA: 16:9 of the node's width, whatever the options below */}
      <div className="relative shrink-0 w-full aspect-video bg-cream-50 flex items-center justify-center overflow-hidden border-b border-cream-200" data-node-preview>
        {status === 'generating' ? (
           <div className="flex flex-col items-center gap-3 p-4">
              {/* ui-ok: the orb is the loader, drawn at its own size; it is not an icon */}
              <GlassOrb size={56} />
              <div className="text-xs font-medium text-ink-soft text-center">Dreaming...</div>
           </div>
        ) : (isStillResolving && rawGeneratedImages.length > 0) ? (
           <div className="flex flex-col items-center gap-3 p-4 animate-pulse">
              <span className="ns-mark"><Loader2 className="animate-spin" /></span>
              <div className="text-xs font-medium text-ink-soft text-center">Loading image...</div>
           </div>
        ) : currentImage ? (
          <div className="relative w-full h-full group">
              <FileImage src={currentImage} userId={userId} workspaceName={workspaceName} className="w-full h-full object-contain pointer-events-none select-none" alt="Generated" fallback={<div className="w-full h-full bg-cream-100 flex items-center justify-center"><AlertCircle size={32} className="text-ink-faint" /></div>} />
              
              {/* Navigation Arrows */}
              {generatedImages.length > 1 && (
                  <>
                      <button 
                          onClick={(e) => { 
                              e.stopPropagation(); 
                              const newIndex = (selectedImageIndex - 1 + generatedImages.length) % generatedImages.length;
                              handleSelectImage(newIndex);
                          }}
                          className="node-reveal ns-icon-btn ns-icon-btn-dark absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label="Previous image"
                      >
                          <ChevronLeft />
                      </button>
                      <button 
                          onClick={(e) => { 
                              e.stopPropagation(); 
                              const newIndex = (selectedImageIndex + 1) % generatedImages.length;
                              handleSelectImage(newIndex);
                          }}
                          className="node-reveal ns-icon-btn ns-icon-btn-dark absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label="Next image"
                      >
                          <ChevronRight />
                      </button>
                  </>
              )}

              {/* Navigation Dots if multiple */}
              {generatedImages.length > 1 && (
                  <div className="ns-glass-dark absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1.5 rounded-full z-10">
                      {generatedImages.map((_: string, idx: number) => (
                          <button 
                              key={idx}
                              onClick={(e) => { e.stopPropagation(); handleSelectImage(idx); }}
                              className={`ns-round w-2 h-2 ${idx === selectedImageIndex ? 'bg-white scale-110' : 'bg-white/40 hover:bg-white/60'}`}
                              aria-label={`Show image ${idx + 1}`}
                          />
                      ))}
                  </div>
              )}

              {/* Counter, share and save: one dark glass bar over the picture, at the top right as in the Video
                  node (at the bottom it lay over the dots on a node of the default width) */}
              <div className="node-reveal ns-glass-dark absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 px-1 py-1 rounded-full">
                  <div className="px-2 text-2xs font-medium tabular-nums">
                      {selectedImageIndex + 1} / {generatedImages.length}
                  </div>
                  <button
                      onClick={(e) => { e.stopPropagation(); setShowTwitterModal(true); }}
                      className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-dark"
                      title="Share to Twitter"
                      aria-label="Share to Twitter"
                  >
                      <Twitter />
                  </button>
                  <a href={currentImage} download={`generated-${Date.now()}-${selectedImageIndex}.png`} className="ns-btn ns-btn-ghost-dark ns-btn-xs h-6">
                      <Download size={14} /> Save
                  </a>
              </div>
          </div>
        ) : (
          <div className="ns-empty select-none" data-stage-empty>
            <span className="ns-mark ns-mark-lg"><ImageIcon /></span>
            <p className="ns-empty-text">No image yet</p>
          </div>
        )}
        
        {/* Error Overlay */}
        {status === 'error' && data.errorMessage && (
             <div className="absolute inset-0 bg-white/95 z-10 flex flex-col items-center justify-center p-4 text-center">
                 <span className="ns-mark ns-mark-sm mb-2" data-tone="danger"><AlertCircle /></span>
                 {/* A long message is cut after six lines, never through a line; all of it is in the title */}
                 <p className="max-w-full text-xs font-medium text-danger-deep break-words line-clamp-6" title={data.errorMessage}>{data.errorMessage}</p>
                 <button onClick={() => updateData({ generationStatus: 'idle', errorMessage: undefined })} className="ns-btn ns-btn-ghost ns-btn-xs mt-3">Dismiss</button>
             </div>
        )}

        {/* API Key Prompt */}
        {showApiKeyPrompt && (
          <div className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center p-4 text-center">
            <span className="ns-mark ns-mark-sm mb-2" data-tone="warn"><AlertCircle /></span>
            <p className="ns-t-title mb-2">
              {missingKeyType === 'openai' ? 'OpenAI' : missingKeyType === 'venice' ? 'Venice AI' : 'Google'} key required
            </p>
            <button onClick={handleSelectKey} className="ns-btn ns-btn-primary ns-btn-xs">Set key</button>
          </div>
        )}
      </div>

      {/* CONTROLS keep their own height; the prompt field takes the room left */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="flex-1 flex flex-col gap-2.5 px-3 pt-3" data-node-controls>
        {/* References: the tile chooses from the workspace; connected and chosen pictures beside it */}
        {(inputImages.length > 0 || inputEntities.length > 0 || canPickReferences) && (
            <div className="flex flex-wrap items-start gap-2" data-input-tiles>
                {(canPickReferences || modelDef.supportsImageReference) && (
                    <InputTile
                        icon={<Images size={14} />}
                        label="Image refs"
                        count={inputImages.length}
                        onClick={refPicker.available && !isGenerating ? (anchor) => refPicker.open(anchor, ['image']) : undefined}
                        disabled={isGenerating}
                        title={modelDef.supportsImageReference ? `Pictures to work from: ${inputImages.length} attached. Connect image nodes or choose references.` : `${modelDef.label} does not take reference pictures.`}
                    />
                )}
                {inputImages.map((img, idx) => {
                    // A picture the prompt can name shows its tag, and a click puts the tag in the prompt
                    const tag = tagRefs[idx]?.tag;
                    const canTag = !!tag && !isGenerating;
                    return (
                        <div key={`${img.id}-${idx}`} className={`relative group/tile w-[60px] ${!modelDef.supportsImageReference ? 'opacity-50 grayscale' : ''}`}>
                            <div
                                className={`w-[60px] h-[60px] rounded-lg overflow-hidden bg-cream-200 ring-1 ring-cream-300 ${canTag ? 'cursor-pointer hover:ring-lavender' : ''}`}
                                role={canTag ? 'button' : undefined}
                                tabIndex={canTag ? 0 : undefined}
                                title={canTag ? `Insert ${tag} into the prompt` : undefined}
                                onClick={canTag ? () => tags.insertTag(tag) : undefined}
                                onKeyDown={canTag ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tags.insertTag(tag); } } : undefined}
                            >
                                <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" alt="" />
                            </div>
                            <TileRemoveButton label={`Remove ${img.label}`} onClick={() => onRemoveConnection && onRemoveConnection(img.id)} disabled={isGenerating} />
                            <span
                                className={`block mt-0.5 text-2xs text-center ${tag ? 'whitespace-nowrap text-ink font-semibold' : 'text-ink-soft truncate'}`}
                                title={img.label}
                                data-mention-tag={tag}
                            >
                                {tag ?? img.label}
                            </span>
                        </div>
                    );
                })}
                {inputEntities.map(e => {
                    const name = e.data.entityData?.name || '?';
                    const isActive = prompt.toLowerCase().includes(name.toLowerCase());
                    return (
                        <div
                            key={e.id}
                            className={`ns-chip self-center ${isActive ? 'ns-chip-tone font-semibold' : ''}`}
                            title={isActive ? 'In use: its description is added to the prompt' : 'Mention the name in the prompt to use it'}
                        >
                            {name}
                        </div>
                    );
                })}
            </div>
        )}
        {inputImages.length > 0 && !modelDef.supportsImageReference && (
            <p className="ns-t-meta -mt-1">{modelDef.label} does not take reference pictures; the connected ones are not used.</p>
        )}

        {/* Prompt: it takes the room that is left */}
        <div className="flex-1 flex flex-col min-h-[104px] relative" data-prompt-box>
          <textarea
            ref={promptRef}
            className={`${promptFieldClass} relative z-[1]`}
            placeholder={tags.enabled ? 'Describe your image. Type @ to tag a reference, e.g. "the bird of @Image1"...' : 'Describe your image...'}
            value={prompt}
            onChange={(e) => { markPromptEdit(); updateData({ prompt: e.target.value }); tags.onFieldChange(e.target); }}
            {...tags.fieldProps}
            disabled={isGenerating}
            aria-label="Prompt"
            autoComplete="off"
            data-lpignore="true"
            data-form-type="other"
          />
          <PromptTagLayer tags={tags} />
        </div>
        {tags.enabled && tags.unknownTags.length > 0 && (
            <p className="ns-note" data-tone="warn">
                <AlertCircle aria-hidden="true" />
                <span className="min-w-0">{tags.unknownTags.join(', ')} {tags.unknownTags.length === 1 ? 'names' : 'name'} no attached reference. Attached: {tagRefs.map(ref => ref.tag).join(', ')}.</span>
            </p>
        )}
        {currentModelId === 'comfyui-custom-workflow' && !selectedCustomWorkflow && (
            <p className="ns-note" data-tone="warn">
                <AlertCircle />
                <span>{customWorkflows.length > 0 ? 'Choose a custom workflow in the model menu to generate.' : 'Add workflow JSON files in Settings, Local models, Custom workflows.'}</span>
            </p>
        )}
        </div>

        {/* The list that opens as @ is typed */}
        <PromptTagPicker tags={tags} />

        {/* Settings as chips, then the price, the balance and the send button */}
        <SettingsRow>
            <SettingChip
                icon={currentModelId === 'comfyui-custom-workflow' ? <Workflow size={14} /> : <Sparkles size={14} />}
                label="Model"
                value={modelSelectValue}
                display={currentModelId === 'comfyui-custom-workflow' ? (selectedCustomWorkflow?.name ?? 'Choose a workflow') : shortModelLabel(modelDef.label)}
                options={modelOptions}
                onChange={handleModelSelect}
                disabled={isGenerating}
            />
            {modelDef.supportedRatios && (
                <SettingChip icon={<RectangleHorizontal size={14} />} label="Aspect ratio" value={aspectRatio} display={aspectRatio} options={modelDef.supportedRatios} onChange={(v) => updateConfig('aspectRatio', v)} disabled={isGenerating} />
            )}
            {modelDef.supportedSizes && (
                <SettingChip icon={<RectangleHorizontal size={14} />} label="Size" value={imageSize} display={imageSize.replace('x', '×')} options={modelDef.supportedSizes} onChange={(v) => updateConfig('size', v)} disabled={isGenerating} />
            )}
            {modelDef.supportedResolutions && (
                <SettingChip icon={<Scan size={14} />} label="Resolution" value={imageResolution} options={modelDef.supportedResolutions} onChange={(v) => updateConfig('resolution', v)} disabled={isGenerating} />
            )}
            {modelDef.supportedQualities && (
                <SettingChip icon={<Gauge size={14} />} label="Quality" value={imageQuality} options={modelDef.supportedQualities} onChange={(v) => updateConfig('quality', v)} disabled={isGenerating} />
            )}
            <SettingChip
                icon={<Images size={14} />}
                label="Number of images"
                value={String(numberOfImages)}
                display={String(numberOfImages)}
                options={Array.from({ length: modelDef.maxGeneratedImages || 4 }, (_, i) => ({ value: String(i + 1), label: `${i + 1} image${i ? 's' : ''}` }))}
                onChange={(v) => updateConfig('numberOfImages', Number(v))}
                disabled={isGenerating}
            />
            {modelDef.provider === 'comfyui' && (
                <SettingChip
                    icon={<SlidersHorizontal size={14} />}
                    label="Steps"
                    value={String(steps)}
                    display={`${steps} steps`}
                    options={stepOptions(steps)}
                    onChange={(v) => updateConfig('steps', Number(v))}
                    disabled={isGenerating}
                />
            )}
            <SendGroup
                onSend={generateImage}
                busy={isGenerating}
                disabled={!canGenerate}
                onStop={modelDef.provider === 'comfyui' ? async () => {
                    try {
                        isCancelledRef.current = true; // Mark as cancelled
                        const client = getComfyUIClient();
                        await client.interrupt();
                        updateData({ generationStatus: 'idle', errorMessage: undefined });
                    } catch (e) {
                        console.error("Failed to stop generation:", e);
                    }
                } : undefined}
                price={
                    <>
                        ${estimatedCost.toFixed(3)}
                        {modelDef.provider === 'venice' && veniceApiKey && <> · <VeniceBalanceLabel apiKey={veniceApiKey} estimateUsd={estimatedCost} /></>}
                    </>
                }
            />
        </SettingsRow>
      </div>
      {refPicker.picker}

      {/* Twitter Share Modal */}
      {currentImage && (
        <ShareToTwitterModal
          isOpen={showTwitterModal}
          onClose={() => setShowTwitterModal(false)}
          mediaUrl={currentImage}
          mediaType="image"
          defaultCaption={prompt ? `Created with AI: ${prompt.slice(0, 200)}${prompt.length > 200 ? '...' : ''}` : ''}
        />
      )}
    </div>
  );
};